/**
 * Canonical Canvas Input Ingestion Contract
 * (VISTOERA_CANVAS_PROJECT_SWITCH_DIVERGENCE_AND_STRUCTURED_ASSET_INGESTION_CONTRACT_P0_V1)
 *
 * Invariant: for semantic input edges,
 *   EDGE_EXISTS ⇔ SEMANTIC_INPUT_BINDING_EXISTS
 *
 * Visual React Flow edges and target business fields must mutate together.
 */

import type { Edge, Node } from '@xyflow/react'
import type { FlowNodeData } from './types'
import { CREATIVE_CONTEXT_EDGE_RELATION } from './scriptGenerationCanvas'
import { SCRIPT_SOURCE_EDGE_RELATION, resolveScriptSourceContext } from './scriptSourceContext'
import { SCRIPT_CONTEXT_EDGE_RELATION } from './storyboardGenerationCanvas'

/** Edge relation for image/media → generate_creative Related Assets. */
export const RELATED_ASSET_EDGE_RELATION = 'RELATED_ASSET'

export type CanvasInputSlot =
  | 'RELATED_ASSETS'
  | 'PRIMARY_SCRIPT_SOURCE'
  | 'PRIMARY_CREATIVE_CONTEXT'
  | 'PRIMARY_SCRIPT_CONTEXT'
  | 'VOICE_TEMPLATE'
  | 'REFERENCE_IMAGE'
  | 'TRYON_PERSON'
  | 'TRYON_GARMENT'
  | 'MOTION_VIDEO'
  /** VISTOERA_CANVAS_PIN_EXPANDED_AND_GENERATE_VIDEO_STRUCTURED_ASSET_INPUT_PORTS_REFERENCE_VIDEO_FOUNDATION_P0_V1
   * §P7 item 7/§P13 — `video_asset -> generate_video`'s dedicated
   * `reference-video-in` handle. `generate_image`/`image_asset` →
   * `character-in`/`product-in`/`scene-in`/`reference-asset-in` and
   * `generate_video` (GENERATED_VIDEO) → `reference-video-in` are
   * deliberately NOT added as rows here — neither `generate_image` nor
   * `generate_video` is a member of `INGESTION_MATRIX_SOURCES`
   * (`CanvasBoard.tsx`'s `evaluateConnection` only consults this matrix
   * when BOTH sides already appear in it), so those pairs correctly
   * fall through to the Typed Dataflow gate (`dataflow.ts`'s
   * `validateDropZoneTarget`) alone — the same path the pre-existing
   * KEYFRAME/TRYON handles already use. Only `video_asset` needed a row
   * here because it's already a matrix SOURCE (via the
   * `motion_reconstruction` row below) and `generate_video` is already
   * a matrix TARGET (via `REFERENCE_IMAGE`) — omitting this pair would
   * make `video_asset -> generate_video` fail at the FIRST gate with
   * "此资产不能作为该节点的输入" before `validateDropZoneTarget` ever runs. */
  | 'REFERENCE_VIDEO'
  | 'GENERIC_INPUT'
  | 'UNSUPPORTED'

export interface CanvasInputCompatibilityRow {
  sourceType: string
  targetType: string
  slot: CanvasInputSlot
  edgeRelation: string
  supported: boolean
  notes?: string
}

/** Inventory matrix — unsupported pairs must not create semantic edges. */
export const CANVAS_INPUT_COMPATIBILITY_MATRIX: CanvasInputCompatibilityRow[] = [
  { sourceType: 'image_asset', targetType: 'generate_creative', slot: 'RELATED_ASSETS', edgeRelation: RELATED_ASSET_EDGE_RELATION, supported: true },
  { sourceType: 'image_asset', targetType: 'project_start', slot: 'RELATED_ASSETS', edgeRelation: RELATED_ASSET_EDGE_RELATION, supported: true },
  { sourceType: 'image_asset', targetType: 'script_generation', slot: 'RELATED_ASSETS', edgeRelation: RELATED_ASSET_EDGE_RELATION, supported: true },
  { sourceType: 'image_asset', targetType: 'storyboard_generation', slot: 'RELATED_ASSETS', edgeRelation: RELATED_ASSET_EDGE_RELATION, supported: true },
  { sourceType: 'image_asset', targetType: 'generate_image', slot: 'REFERENCE_IMAGE', edgeRelation: 'REFERENCES', supported: true },
  { sourceType: 'image_asset', targetType: 'generate_video', slot: 'REFERENCE_IMAGE', edgeRelation: 'REFERENCES', supported: true },
  { sourceType: 'image_asset', targetType: 'generate_tryon', slot: 'TRYON_PERSON', edgeRelation: 'INPUT_TO', supported: true, notes: 'Handle-specific person/garment' },
  { sourceType: 'generate_creative', targetType: 'script_generation', slot: 'PRIMARY_SCRIPT_SOURCE', edgeRelation: SCRIPT_SOURCE_EDGE_RELATION, supported: true },
  { sourceType: 'creative_candidate', targetType: 'script_generation', slot: 'PRIMARY_SCRIPT_SOURCE', edgeRelation: SCRIPT_SOURCE_EDGE_RELATION, supported: true },
  { sourceType: 'text_asset', targetType: 'script_generation', slot: 'PRIMARY_SCRIPT_SOURCE', edgeRelation: SCRIPT_SOURCE_EDGE_RELATION, supported: true },
  { sourceType: 'script_generation', targetType: 'storyboard_generation', slot: 'PRIMARY_SCRIPT_CONTEXT', edgeRelation: SCRIPT_CONTEXT_EDGE_RELATION, supported: true },
  { sourceType: 'generate_voice_template', targetType: 'generate_voice', slot: 'VOICE_TEMPLATE', edgeRelation: 'VOICE_TEMPLATE', supported: true },
  { sourceType: 'video_asset', targetType: 'motion_reconstruction', slot: 'MOTION_VIDEO', edgeRelation: 'INPUT_TO', supported: true },
  {
    sourceType: 'video_asset',
    targetType: 'generate_video',
    slot: 'REFERENCE_VIDEO',
    edgeRelation: 'REFERENCES',
    supported: true,
    notes: 'Handle-specific: reference-video-in only (dataflow.ts validateDropZoneTarget)',
  },
]

export function resolveCanvasInputSlot(sourceType: string, targetType: string): CanvasInputCompatibilityRow | null {
  return (
    CANVAS_INPUT_COMPATIBILITY_MATRIX.find(
      (row) => row.supported && row.sourceType === sourceType && row.targetType === targetType,
    ) ?? null
  )
}

export function isCanvasInputCompatible(sourceType: string, targetType: string): boolean {
  return resolveCanvasInputSlot(sourceType, targetType) != null
}

export interface RelatedAssetRef {
  asset_id: string
  picker_item_id?: string
  asset_type: string
  display_name: string
  source_node_id?: string
}

export function buildRelatedAssetFromImageNode(source: Node): RelatedAssetRef | null {
  const data = source.data as unknown as FlowNodeData
  if (data.nodeType !== 'image_asset' || !data.entityId) return null
  const label =
    (typeof data.fields?.label === 'string' && data.fields.label) ||
    data.entityId
  return {
    asset_id: data.entityId,
    picker_item_id: `sa:${data.entityId}`,
    asset_type: 'image_asset',
    display_name: label,
    source_node_id: source.id,
  }
}

/**
 * True when a script_generation node has a visual upstream Creative edge
 * (DERIVED_FROM or legacy INPUT_TO) but no hydrated creativeContext —
 * the P0 orphan-edge failure mode.
 */
export function scriptNeedsCreativeContextReconcile(
  target: Node,
  edges: Edge[],
  nodes: Node[],
): boolean {
  const data = target.data as unknown as FlowNodeData
  if (data.nodeType !== 'script_generation') return false
  const fields = (data.fields || {}) as Record<string, unknown>
  if (resolveScriptSourceContext(fields)) return false
  return edges.some((e) => {
    if (e.target !== target.id) return false
    const relation = (e.data as { relationType?: string } | undefined)?.relationType
    if (relation !== CREATIVE_CONTEXT_EDGE_RELATION && relation !== SCRIPT_SOURCE_EDGE_RELATION && relation !== 'INPUT_TO') {
      return false
    }
    const source = nodes.find((n) => n.id === e.source)
    if (!source) return false
    const st = nodeTypeOf(source)
    return st === 'generate_creative' || st === 'creative_candidate' || st === 'text_asset'
  })
}

/**
 * True when a storyboard_generation node has a visual upstream Script edge
 * (DERIVED_FROM / INPUT_TO) but no hydrated scriptContext —
 * the Script→Storyboard orphan-edge P0 failure mode.
 */
export function storyboardNeedsScriptContextReconcile(
  target: Node,
  edges: Edge[],
  nodes: Node[],
): boolean {
  const data = target.data as unknown as FlowNodeData
  if (data.nodeType !== 'storyboard_generation') return false
  const fields = (data.fields || {}) as Record<string, unknown>
  if (fields.scriptContext) return false
  return edges.some((e) => {
    if (e.target !== target.id) return false
    const relation = (e.data as { relationType?: string } | undefined)?.relationType
    if (relation !== SCRIPT_CONTEXT_EDGE_RELATION && relation !== 'INPUT_TO') return false
    const source = nodes.find((n) => n.id === e.source)
    if (!source) return false
    return nodeTypeOf(source) === 'script_generation'
  })
}

/** Field bag key used for RELATED_ASSET edge binding on a target node. */
export function relatedAssetsFieldKey(nodeType?: string | null): 'assets' | 'supportingAssets' {
  if (nodeType === 'script_generation' || nodeType === 'storyboard_generation') {
    return 'supportingAssets'
  }
  return 'assets'
}

/** Merge one related asset into the target node's asset field (dedupe by asset_id). */
export function mergeRelatedAssetIntoFields(
  fields: Record<string, unknown>,
  asset: RelatedAssetRef,
  nodeType?: string | null,
): Record<string, unknown> {
  const key = relatedAssetsFieldKey(nodeType)
  const existing = Array.isArray(fields[key]) ? (fields[key] as RelatedAssetRef[]) : []
  if (existing.some((a) => a.asset_id === asset.asset_id)) {
    return fields
  }
  // Script/storyboard also keep legacy `assets` cleared of this id to avoid dual SoT.
  const next: Record<string, unknown> = { ...fields, [key]: [...existing, asset] }
  if (key === 'supportingAssets' && Array.isArray(fields.assets)) {
    next.assets = (fields.assets as RelatedAssetRef[]).filter((a) => a.asset_id !== asset.asset_id)
  }
  return next
}

/**
 * Replace related-asset list while preserving every other generation/business
 * key (currentResult, generatedOutputId, needsRegeneration, provider, …).
 *
 * Must return `Record<string, unknown>` — an object-literal
 * `{ ...(fields), assets }` is inferred by TypeScript as `{ assets: … }`
 * only (spread of Record loses the index signature), which breaks
 * generation-field reads at compile time.
 */
export function replaceRelatedAssetsInFields(
  fields: Record<string, unknown>,
  assets: RelatedAssetRef[],
  nodeType?: string | null,
): Record<string, unknown> {
  const key = relatedAssetsFieldKey(nodeType)
  const next: Record<string, unknown> = { ...fields, [key]: assets }
  if (key === 'supportingAssets') {
    next.assets = Array.isArray(fields.assets)
      ? (fields.assets as RelatedAssetRef[]).filter((a) => !assets.some((b) => b.asset_id === a.asset_id))
      : fields.assets
  }
  return next
}

/** True when the fields bag carries a generation result identity. */
export function generationFieldsHaveResult(fields: Record<string, unknown>): boolean {
  return !!(fields.currentResult || fields.generatedOutputId || fields.outputAssetId)
}

/** Stamp needsRegeneration when a result already exists (in-place). */
export function markGenerationStaleIfHasResult(fields: Record<string, unknown>): Record<string, unknown> {
  if (generationFieldsHaveResult(fields)) {
    fields.needsRegeneration = true
  }
  return fields
}

/** Remove related asset by asset_id (and optional source node). */
export function removeRelatedAssetFromFields(
  fields: Record<string, unknown>,
  opts: { assetId?: string; sourceNodeId?: string; nodeType?: string | null },
): Record<string, unknown> {
  const key = relatedAssetsFieldKey(opts.nodeType)
  const existing = Array.isArray(fields[key]) ? (fields[key] as RelatedAssetRef[]) : []
  const next = existing.filter((a) => {
    if (opts.assetId && a.asset_id === opts.assetId) return false
    if (opts.sourceNodeId && a.source_node_id === opts.sourceNodeId) return false
    return true
  })
  if (next.length === existing.length) return fields
  return { ...fields, [key]: next }
}

export function relatedAssetEdgeMatches(edge: Edge, sourceId: string, targetId: string): boolean {
  if (edge.source !== sourceId || edge.target !== targetId) return false
  const relation = (edge.data as { relationType?: string } | undefined)?.relationType
  return relation === RELATED_ASSET_EDGE_RELATION || relation === 'INPUT_TO' || relation === 'REFERENCES'
}

/** Build CreativeContextDragPayload-shaped object from a generate_creative node. */
export function buildCreativePayloadFromNode(source: Node): {
  sourceNodeId: string
  sourceRunId: string | null
  assetsSourceNodeId: string
  candidateId: string
  title: string
  premise: string
  hook: string
  tone: string
  storyDirection: string
  productRole: string | null
  characterRole: string | null
  estimatedDuration: number | null
  tags: string[]
  rationale: string
} | null {
  const data = source.data as unknown as FlowNodeData
  if (data.nodeType !== 'generate_creative' && data.nodeType !== 'creative_candidate') return null
  const fields = (data.fields || {}) as Record<string, unknown>
  const currentResult = fields.currentResult as
    | {
        candidateId?: string
        sourceRunId?: string | null
        title?: string
        premise?: string
        hook?: string
        tone?: string
        storyDirection?: string
        productRole?: string | null
        characterRole?: string | null
        estimatedDuration?: number | null
        tags?: string[]
        rationale?: string
      }
    | null
    | undefined

  // Prefer currentResult (generate_creative). Fall back to legacy candidate fields.
  if (currentResult && (currentResult.title || currentResult.premise || currentResult.candidateId)) {
    return {
      sourceNodeId: source.id,
      sourceRunId: currentResult.sourceRunId ?? null,
      assetsSourceNodeId: source.id,
      candidateId: currentResult.candidateId || `candidate-${source.id}`,
      title: currentResult.title || '',
      premise: currentResult.premise || '',
      hook: currentResult.hook || '',
      tone: currentResult.tone || '',
      storyDirection: currentResult.storyDirection || '',
      productRole: currentResult.productRole ?? null,
      characterRole: currentResult.characterRole ?? null,
      estimatedDuration: currentResult.estimatedDuration ?? null,
      tags: Array.isArray(currentResult.tags) ? currentResult.tags : [],
      rationale: currentResult.rationale || '',
    }
  }

  if (data.nodeType === 'creative_candidate') {
    return {
      sourceNodeId: source.id,
      sourceRunId: (fields.sourceRunId as string | null) ?? null,
      assetsSourceNodeId: (fields.rootBriefNodeId as string) || source.id,
      candidateId: (fields.candidateId as string) || source.id,
      title: String(fields.title ?? ''),
      premise: String(fields.premise ?? ''),
      hook: String(fields.hook ?? ''),
      tone: String(fields.tone ?? ''),
      storyDirection: String(fields.storyDirection ?? ''),
      productRole: (fields.productRole as string | null) ?? null,
      characterRole: (fields.characterRole as string | null) ?? null,
      estimatedDuration: (fields.estimatedDuration as number | null) ?? null,
      tags: Array.isArray(fields.tags) ? (fields.tags as string[]) : [],
      rationale: String(fields.rationale ?? ''),
    }
  }

  return null
}

export function nodeTypeOf(node: Node | undefined): string {
  if (!node) return ''
  return (node.data as unknown as FlowNodeData).nodeType || ''
}
